// src/components/Snackbar/NewImplementations/BaseAlert/BaseAlert.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react'
import styles from './styles.module.css'

const ANIM_MS = 300

export default function BaseAlert ({
  open,
  title,
  body,
  actions = [],
  onClose,
  color = '#ef3688', // default error color
  style = {}
}) {
  /* ------------------------------------------------------------
   *  Local render/cache state
   * ---------------------------------------------------------- */
  const [render, setRender] = useState(open)
  const [cache, setCache] = useState({ title, body, actions })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) {
      setCache({ title, body, actions }) // update visible content
      setRender(true) // ensure mounted
    } else {
      // wait for slide-out to finish before unmounting
      const t = setTimeout(() => setRender(false), ANIM_MS)
      return () => clearTimeout(t)
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /* ------------------------------------------------------------
   *  Click-outside & Esc-key handlers
   * ---------------------------------------------------------- */
  const toastRef = useRef(null)

  /** Memoised so add/removeEventListener always get
   * the same* function object. */
  const handleOutside = useCallback(
    e => {
      if (!toastRef.current || toastRef.current.contains(e.target)) return

      // Did the click come from an action button?
      const isRetry = e.target.closest('[data-retry="true"]')

      // Wait full duration only for a retry click
      setTimeout(onClose, isRetry ? ANIM_MS : 0)
    },
    [onClose]
  )

  useEffect(() => {
    if (!open) return
    document.addEventListener('pointerdown', handleOutside)
    return () => document.removeEventListener('pointerdown', handleOutside)
  }, [open, handleOutside])

  if (!render) return null

  return (
    <div
      ref={toastRef}
      role='alertdialog'
      aria-labelledby='alert-title'
      aria-describedby='alert-body'
      className={`${styles.baseAlert_toast} ${
        open ? styles.baseAlert_slideIn : styles.baseAlert_slideOut
      }`}
      style={{
        backgroundColor: color,
        ...style
      }}
    >
      <div className={styles.baseAlert_header}>
        <strong id='alert-title' className={styles.baseAlert_title}>
          {cache.title}
        </strong>
      </div>

      {cache.body && (
        <div id='alert-body' className={styles.baseAlert_body}>
          {cache.body}
        </div>
      )}

      {cache.actions.length > 0 && (
        <div className={styles.baseAlert_actions}>
          {cache.actions.map((a, i) => (
            <button
              key={i}
              onClick={a.onClick}
              className={styles.baseAlert_actionButton}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
